import { memo, useCallback, useRef, type ChangeEvent, type FC } from "react";
import { FileText, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { WhatsAppMediaType } from "src/services/WhatsAppCampaignAPI";
import { formatNumber } from "src/utils/numberFormat";
import { MEDIA_RULES, WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type { WhatsAppMediaState } from "../WhatsAppCampaignTypes";
import { formatFileSizeKb } from "../WhatsAppCampaignUtils";

const MEDIA_KEY = `${WHATSAPP_EDITOR_KEY}.media`;
const FIELD_ID = "wa-media-attachment";
const ERROR_ID = "wa-media-error";

interface MediaAttachmentFieldProps {
  mediaType: WhatsAppMediaType;
  media: WhatsAppMediaState;
  onAttach: (_file: File) => void;
  onRemove: () => void;
}

const MediaAttachmentField: FC<MediaAttachmentFieldProps> = ({
  mediaType,
  media,
  onAttach,
  onRemove,
}) => {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const { attachment, isUploading, error } = media;
  const { accept, maxSizeMb } = MEDIA_RULES[mediaType];
  const format = (value: number) => formatNumber(value, {}, i18n.language);

  const openFilePicker = useCallback(() => inputRef.current?.click(), []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (file) {
        onAttach(file);
      }
    },
    [onAttach]
  );

  const sizeKb = attachment ? format(formatFileSizeKb(attachment.sizeBytes)) : "";
  const dimensions =
    attachment && attachment.width !== null && attachment.height !== null
      ? { width: attachment.width, height: attachment.height }
      : null;

  return (
    <div className="wa-parameter">
      <div className="wa-parameter__header">
        <label htmlFor={FIELD_ID} className="wa-parameter__label">
          {t(`${MEDIA_KEY}.label`)}
          <span className="campaign-flow-field__required" aria-hidden>
            *
          </span>
        </label>
      </div>
      <div className="wa-media">
        {attachment && (
          <div className="wa-media__file">
            {attachment.previewUrl ? (
              <img src={attachment.previewUrl} alt="" className="wa-media__thumbnail" />
            ) : (
              <FileText className="wa-media__thumbnail" aria-hidden />
            )}
            <div className="wa-media__details">
              <span className="wa-media__name">{attachment.fileName}</span>
              <span className="wa-media__meta">
                {dimensions
                  ? t(`${MEDIA_KEY}.details`, {
                      width: format(dimensions.width),
                      height: format(dimensions.height),
                      size: sizeKb,
                    })
                  : t(`${MEDIA_KEY}.detailsNoDimensions`, { size: sizeKb })}
              </span>
            </div>
            <button
              type="button"
              className="wa-media__remove"
              onClick={onRemove}
              aria-label={t(`${MEDIA_KEY}.remove`, { name: attachment.fileName })}
            >
              <Trash2 aria-hidden />
            </button>
          </div>
        )}

        <div className="wa-media__upload">
          <input
            ref={inputRef}
            id={FIELD_ID}
            type="file"
            accept={accept.join(",")}
            onChange={handleFileChange}
            aria-describedby={error ? ERROR_ID : undefined}
            className="wa-media__input"
          />
          <button
            type="button"
            className="wa-media__button"
            onClick={openFilePicker}
            disabled={isUploading}
          >
            {t(
              isUploading
                ? `${MEDIA_KEY}.uploading`
                : attachment
                  ? `${MEDIA_KEY}.replace`
                  : `${MEDIA_KEY}.upload`
            )}
          </button>
          <span className="wa-media__hint">
            {t(`${MEDIA_KEY}.supported.${mediaType}`, { size: format(maxSizeMb) })}
          </span>
        </div>

        {error && (
          <span id={ERROR_ID} role="alert" className="wa-media__error">
            {error}
          </span>
        )}
      </div>
    </div>
  );
};

export default memo(MediaAttachmentField);
